import { skipToken } from '@reduxjs/toolkit/query/react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { SOURCE_TCAPI } from '@rsos/constants/locationSources';
import { useGetTCAPIConversationMetadataQuery } from '@rsos/reduxDataStore/src/features/conversations/tcapiAPI';
import {
  getIsConversationTimedOut,
  useGetConversation,
  useHasConversationBeenInitiated,
} from '@rsos/reduxDataStore/src/features/conversations/utils';
import { useSelectedCall } from '@rsos/reduxDataStore/src/features/emergencyCalls';
import { StateWithEmergencyCalls } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import { getMostRecentLoc } from '@rsos/utils/getMostRecentLoc';
import isTextBasedSource from '../../../../utils/isSourceSMS';
import AllMessages from '../../../PreconfiguredMessages/AllMessages';
import { useEnabledPreconfiguredMessages } from '../../../PreconfiguredMessages/useEnabledPreconfiguredMessages';
import { useChatContext } from '../ChatContext';
import ConversationsChat from './ConversationsChat';
import ConversationsChatHeader from './ConversationsChatHeader';
import {
  AbsoluteAllMessagesContainer,
  ConversationsContainer,
} from './ConversationsChatbox.styles';
import TransferBanner from './TransferBanner';

const ConversationsChatBox = () => {
  const callerID = useSelector(
    (state: StateWithEmergencyCalls) => state.emergencyCalls.selectedCallerID,
  );

  const selectedCall = useSelectedCall();
  const mostRecentLocation = getMostRecentLoc(selectedCall);

  const hasRequestLocation = Boolean(
    isTextBasedSource(mostRecentLocation?.source),
  );

  const orgName = useCurrentOrgName();

  const enabledPreconfiguredMessages = useEnabledPreconfiguredMessages(
    orgName,
    { hasRequestLocation },
  );

  const isConversationStarted = useHasConversationBeenInitiated(callerID);
  const conversation = useGetConversation(callerID);

  // Keep an active RTK Query subscription while the chatbox is open so that
  // tag invalidation (from mutations and WS messages) reliably triggers
  // refetches. Without this, the subscription from EmergencyCall.js can drop
  // during certain re-renders, causing util.invalidateTags to call
  // removeQueryResult instead of refetching.
  useGetTCAPIConversationMetadataQuery(
    callerID && orgName && conversation?.source === SOURCE_TCAPI
      ? { callerID, orgName }
      : skipToken,
  );

  const {
    collapse,
    isCollapsed,
    isAllMessagesVisible,
    setIsAllMessagesVisible,
    onCopyToChat,
    onSendMessage,
  } = useChatContext();

  const handleViewClose = () => {
    setIsAllMessagesVisible(false);
  };

  useEffect(() => {
    if (
      conversation?.source === SOURCE_TCAPI &&
      getIsConversationTimedOut(conversation) &&
      !isCollapsed
    ) {
      collapse?.(true);
    }
  }, [collapse, conversation, isCollapsed]);

  return (
    <>
      <TransferBanner conversation={conversation} />
      <ConversationsContainer
        data-name="conversations-container"
        flex={isCollapsed ? '0' : '1'}
      >
        <ConversationsChatHeader
          isConversationStarted={isConversationStarted}
        />
        {isConversationStarted && (
          <ConversationsChat
            hasRequestLocationAccess={hasRequestLocation}
            preconfiguredMessages={enabledPreconfiguredMessages}
          />
        )}
        {isAllMessagesVisible && (
          <AbsoluteAllMessagesContainer>
            <AllMessages
              preconfiguredMessages={enabledPreconfiguredMessages}
              onClose={handleViewClose}
              onCopyToChat={onCopyToChat}
              onSendMessage={onSendMessage}
            />
          </AbsoluteAllMessagesContainer>
        )}
      </ConversationsContainer>
    </>
  );
};

export default ConversationsChatBox;
